import { type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { useDeleteFeature } from '@/features/features/hooks/features';
import { useFeatureDetailViewModel } from '@/features/features/hooks/useFeatureDetailViewModel';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects';
import { useEntityDetailPageScaffold } from '@/features/shared/hooks/useEntityDetailPageScaffold';
import { DetailPageScaffold } from '@/ui/composites/scaffolds';
import { FeatureDetailContent } from './FeatureDetailContent';
import type { AuthSession } from '@/auth/types';

type FeatureDetailPageProps = { session: AuthSession | null };

export const FeatureDetailPage = ({ session }: FeatureDetailPageProps): ReactElement => {
  const { slug } = useParams<{ slug: string }>();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const deleteFeature = useDeleteFeature();
  const { featureQuery, feature } = useFeatureDetailViewModel({
    slug,
    selectedOrganisationId,
    selectedProjectId,
    setSelectedProjectId,
  });
  const detailPage = useEntityDetailPageScaffold({
    load: {
      data: feature,
      isPending: featureQuery.isPending,
      isFetched: featureQuery.isFetched,
    },
    delete: {
      mutation: deleteFeature,
      successMessage: 'Feature deleted.',
      fallbackErrorMessage: 'Failed to delete feature.',
    },
    redirects: {
      onDeleteTo: '/features',
      notFoundBackLabel: 'Back to features',
    },
    notFoundCopy: {
      title: 'Feature not found',
      body: 'This feature does not exist or you no longer have access to it.',
    },
  });

  if (detailPage.showNotFound) return detailPage.renderNotFound();

  return (
    <DetailPageScaffold
      session={session}
      sessionMessage="Sign in to view feature details."
      organisationId={selectedOrganisationId}
      projectId={selectedProjectId}
      isPending={featureQuery.isPending}
      isError={Boolean(featureQuery.error)}
      error={featureQuery.error}
      errorMessage="Failed to load feature"
      data={feature}
    >
      {(resolvedFeature) => {
        return (
          <FeatureDetailContent
            title={resolvedFeature.name}
            tags={resolvedFeature.tags}
            onDelete={detailPage.handleDelete}
            deleteLoading={detailPage.deleteLoading}
          />
        );
      }}
    </DetailPageScaffold>
  );
};
